import React, { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import { Button } from '../../ui/Button/Button';
import { Modal, ModalActions } from '../../ui/Modal/Modal';
import styles from './NewsFormModal.module.scss';

const TITLE_MAX = 200;

interface NewsFormModalProps {
    open: boolean;
    initialTitle?: string;
    initialText?: string;
    submitting?: boolean;
    onClose: () => void;
    onSubmit: (values: { title: string; text: string }) => void;
}

export const NewsFormModal: FC<NewsFormModalProps> = ({
    open,
    initialTitle = '',
    initialText = '',
    submitting = false,
    onClose,
    onSubmit,
}) => {
    const [title, setTitle] = useState(initialTitle);
    const [text, setText] = useState(initialText);
    const [error, setError] = useState<string | null>(null);
    const titleRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;

        setTitle(initialTitle);
        setText(initialText);
        setError(null);

        const timer = setTimeout(() => titleRef.current?.focus(), 40);
        return () => clearTimeout(timer);
    }, [open, initialTitle, initialText]);

    const submit = (): void => {
        if (!title.trim()) {
            setError('Укажите заголовок');
            titleRef.current?.focus();
            return;
        }

        if (!text.trim()) {
            setError('Текст новости не может быть пустым');
            return;
        }

        onSubmit({ title: title.trim(), text });
    };

    return (
        <Modal
            open={open}
            width={640}
            title={initialTitle ? 'Редактирование новости' : 'Новая новость'}
            onClose={onClose}
            closeOnOverlayClick={false}
            footer={
                <ModalActions>
                    <Button onClick={onClose} disabled={submitting}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={submit} disabled={submitting}>
                        {submitting ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </ModalActions>
            }
        >
            <label className={styles.label} htmlFor="news-title">
                Заголовок
            </label>
            <input
                id="news-title"
                ref={titleRef}
                className={styles.field}
                value={title}
                maxLength={TITLE_MAX}
                placeholder="Напишите заголовок"
                autoComplete="off"
                onChange={(event) => {
                    setTitle(event.target.value);
                    setError(null);
                }}
            />

            <label className={styles.label} htmlFor="news-text">
                Текст
            </label>
            <textarea
                id="news-text"
                className={styles.textarea}
                rows={12}
                value={text}
                placeholder="Напишите текст новости"
                onChange={(event) => {
                    setText(event.target.value);
                    setError(null);
                }}
            />

            <div className={styles.foot}>
                {error ? (
                    <span className={styles.error}>{error}</span>
                ) : (
                    <span className={styles.hint}>Пустая строка разделяет абзацы</span>
                )}
            </div>
        </Modal>
    );
};